import type { Guide, GuideRecordHint } from '../types';

/**
 * S-21.10-22 — wariant tylko, gdy realizacja mieści się w oknach (scenariusz e2e 22, BR-FTL-18), z poprawkami po
 * fali C (decyzje użytkownika 22.10.2026, pkt 3 i 4): obsadzenie bez rekomendacji ostrzega, nie blokuje, a spóźnienie
 * ma dwie liczby z opisem źródła. Rekordy (dane pod slajdy z 22.10.2026): DEMO/2026/10/L01 — załadunek w Grazu
 * za godzinę z godzinnym oknem, więc auta ze świeżą telematyką pewnie nie zdążą nawet z ulgami; wariant DEMO 002
 * „sprzed reguły” (`scenario-state.mjs`); DEMO 003 z obsadzeniem H03 po oknie (odcinek spóźnienia na pasku).
 */
const TO_PLAN: GuideRecordHint = {
  description: 'zlecenie do zaplanowania w FTL z krótkim oknem załadunku',
  listPath: '/user-offers',
  listFilter: { forklift: 'hide', dzial: 'FTL' },
};

const WITH_VARIANT: GuideRecordHint = {
  description: 'zlecenie wstępnie planowane z wariantem, którego realizacja nie mieści się w oknach',
  listPath: '/user-offers',
  listFilter: { forklift: 'hide', dzial: 'FTL' },
};

export const guide: Guide = {
  flagKey: 'variantWithinWindows',
  title: 'Wariant tylko, gdy realizacja mieści się w oknach',
  whatItDoes:
    'Wariant powstaje tylko, gdy realizacja mieści się w oknach zlecenia. Gdy kierowca nie dojedzie na rozładunek (albo na załadunek) przed końcem okna nawet z pełną pulą ulg 561/2006, a dane o jego czasie jazdy są świeże, „Wariant” i „Obsadź” są wyszarzone z powodem. Wynik niepewny — wariant powstaje z ostrzeżeniem. Obsadzenie auta, które jest już wariantem albo wybrane bez rekomendacji, ostrzega, ale nie blokuje: powód nie jest wymagany, a historia zlecenia zapisuje „obsadzono mimo braku czasu”. Spóźnienie ma dwie liczby z opisem: wg bieżącej pozycji i czasu pracy kierowcy (z ulgami) oraz wg planu (start w pierwszym możliwym terminie, bez ulg) — obie mówią, o ile koniec rozładunku wypada po końcu okna rozładunku.',
  audience: ['planista FTL', 'Team Leader (planista główny)'],
  presentationItems: ['S-21.10-22'],
  steps: [
    {
      route: '/user-offers/:orderId/show',
      record: TO_PLAN,
      target: 'ftl-zlecenie-dodaj-wariant',
      mockupTestId: 'ftl-zlecenie-dodaj-wariant',
      title: '„Wariant” wyszarzony przy pewnym spóźnieniu',
      description:
        'Tu okno załadunku kończy się za dwie godziny, a pojazdy są kilkaset kilometrów dalej — żaden nie zdąży nawet z ulgami. „Wariant” jest wyszarzony, a dymek podaje powód i obie liczby spóźnienia końca rozładunku: wg bieżącej pozycji i czasu pracy kierowcy (z ulgami — przyjazd w nawiasie) oraz wg planu (start w pierwszym możliwym terminie, bez ulg).',
      action: 'Kliknij „Pokaż rekomendowane pojazdy” i najedź na „Wariant” przy pojeździe.',
      recorder: {
        account: 'master',
        demo: { kind: 'order', number: 'DEMO/2026/10/L01' },
        actions: [{ kind: 'clickButton', name: 'Pokaż rekomendowane pojazdy' }],
      },
    },
    {
      route: '/user-offers/:orderId/show',
      record: TO_PLAN,
      target: 'ftl-zlecenie-obsadz-*',
      mockupTestId: 'ftl-zlecenie-obsadz-{id}',
      title: '„Obsadź” z tym samym powodem',
      description:
        '„Obsadź” przy pojeździe, który nie jest jeszcze wariantem, zakłada wariant — dlatego wyszarza się z tym samym powodem. Przy pojeździe, który już jest wariantem, „Obsadź” zostaje aktywne.',
      action: 'Najedź na „Obsadź” przy pojeździe — dymek podaje powód albo opis obsadzenia.',
      recorder: {
        account: 'master',
        demo: { kind: 'order', number: 'DEMO/2026/10/L01' },
        actions: [{ kind: 'clickButton', name: 'Pokaż rekomendowane pojazdy' }],
      },
    },
    {
      route: '/user-offers',
      target: 'row-assign-late-*',
      title: '„Obsadź…” — ostrzeżenie zamiast blokady',
      description:
        'Wariant dodany wcześniej może dziś pewnie nie zmieścić się w oknach. Okno „Obsadź…” w wierszu banku ostrzega przy nim z obiema liczbami spóźnienia, ale „Obsadź tym pojazdem” zostaje aktywne — Team Leader obsadza bez podawania powodu, a historia zlecenia zapisuje „obsadzono mimo braku czasu” z liczbami.',
      action: 'W banku zleceń przy zleceniu kliknij „Obsadź…” i przeczytaj ostrzeżenie przy wariancie.',
      recorder: {
        account: 'master',
        actions: [{ kind: 'clickButton', name: 'Obsadź…', rowText: 'DEMO/2026/10/L01' }],
      },
    },
    {
      route: '/user-offers/:orderId/show',
      record: WITH_VARIANT,
      target: 'assign-late-warning',
      title: '„Obsadź” na karcie zlecenia — ostrzeżenie i okno',
      description:
        'Po wyborze takiego wariantu w „Obsadź pojazdem” karta pokazuje ostrzeżenie z obiema liczbami, a „Obsadź” otwiera okno „Obsadzić mimo braku czasu?” — bez pola powodu. To samo przy „Zmień pojazd”.',
      action: 'Wybierz wariant w „Obsadź pojazdem”, kliknij „Obsadź”, a w oknie „Obsadź mimo braku czasu” albo „Anuluj”.',
      recorder: {
        account: 'master',
        demo: { kind: 'order', number: 'DEMO/2026/10/L01' },
        actions: [{ kind: 'select', label: 'Obsadź pojazdem', option: 'DEMO 002', prefix: true }],
      },
    },
    {
      route: '/cars/:carId/show',
      record: {
        description: 'pojazd z obsadzeniem, którego szacunek kończy się po oknie zlecenia',
        listPath: '/cars',
      },
      target: 'gantt-late-*',
      title: 'Spóźnienie na pasku planu — dwie liczby',
      description:
        'Kreskowany odcinek za oknem to spóźnienie wg planu. Dymek paska zlecenia podaje teraz obie liczby, każdą z opisem: wg bieżącej pozycji i czasu pracy kierowcy (z ulgami) i wg planu (start w pierwszym możliwym terminie, bez ulg). Obie mierzą to samo — o ile koniec rozładunku wypada po końcu okna rozładunku. Gdy którejś nie da się policzyć — „brak danych” z powodem.',
      action: 'Na karcie pojazdu najedź na pasek zlecenia z kreskowanym odcinkiem za oknem.',
      recorder: { account: 'master', demo: { kind: 'car', plate: 'DEMO 003' } },
    },
  ],
};
